'use client';

import type React from 'react';
import { useEffect, useState } from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface Props {
  value: number; // Price in cents
  onChange: (priceInCents: number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

export function PriceAmountInput({ value, onChange, placeholder = '0.00', disabled = false, className = '' }: Props) {
  const [displayValue, setDisplayValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Convert cents to dollar display value
  useEffect(() => {
    if (!isFocused) {
      const dollarValue = value / 100;
      setDisplayValue(dollarValue.toFixed(2));
    }
  }, [value, isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow empty string for clearing
    if (inputValue === '') {
      setDisplayValue('');
      onChange(0);
      return;
    }

    // Only allow numbers and one decimal point
    const regex = /^\d*\.?\d{0,2}$/;
    if (!regex.test(inputValue)) {
      return;
    }

    setDisplayValue(inputValue);

    // Convert to cents and call onChange
    const dollarValue = Number.parseFloat(inputValue) || 0;
    const centsValue = Math.round(dollarValue * 100);
    onChange(centsValue);
  };

  const handleFocus = () => {
    setIsFocused(true);
    // If the value is 0, clear the display for easier input
    if (value === 0) {
      setDisplayValue('');
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Format the display value when losing focus
    if (displayValue === '') {
      setDisplayValue('0.00');
      onChange(0);
    } else {
      const dollarValue = Number.parseFloat(displayValue) || 0;
      const centsValue = Math.round(dollarValue * 100);
      setDisplayValue((centsValue / 100).toFixed(2));
      onChange(centsValue);
    }
  };

  return (
    <div className={cn('relative', className)}>
      <span className='absolute top-1 left-3 transform text-muted-foreground'>$</span>
      <Input
        id='price-input'
        type='text'
        value={displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        className='pl-8'
      />
    </div>
  );
}
